import { AlertCircle, BookOpenCheck, LoaderCircle } from "lucide-react";
import { useState } from "react";
import type { DealContext } from "../../../api/schemas.js";
import { Button } from "../../../components/ui/button.js";

export interface DealEnrollmentSetupProps {
  readonly context: DealContext;
  readonly busy: boolean;
  /** addon: у сделки уже есть план, добавляется докупленный курс. */
  readonly variant?: "initial" | "addon";
  /** Курсы на выбор; по умолчанию — все курсы сделки. Для докупки — те, что ещё без зачисления. */
  readonly availableCourses?: DealContext["courses"];
  /** The course to enroll on; omitted when the deal leaves no choice. */
  readonly onCreate: (courseElementId?: number) => void;
}

const FIELD_LABELS = {
  location: "Локация",
  vehicle: "Техника",
  course: "Курс",
  venue: "Площадка",
  instructor: "Инструктор",
} as const;

type SetupField = keyof typeof FIELD_LABELS;

function courseSummary(course: DealContext["courses"][number]): string {
  const parts: string[] = [];
  if (course.lessonCount !== null && course.lessonCount > 0) {
    parts.push(`${course.lessonCount} урок${course.lessonCount === 1 ? "" : "ов"}`);
  }
  if (course.priceLocal !== null) parts.push(course.priceLocal.toLocaleString("ru-RU"));
  if (course.quantity > 1) parts.push(`× ${course.quantity}`);
  return parts.join(" · ");
}

export function DealEnrollmentSetup(props: DealEnrollmentSetupProps) {
  const missing = new Set<SetupField>(props.context.missingDealData);
  if (props.context.venueId === null) missing.add("venue");
  if (props.context.instructorId === null) missing.add("instructor");
  const missingFields = [...missing];

  const addon = props.variant === "addon";
  // Доп. опции («Права») в выбор не попадают: плана у них не бывает.
  const courses = (props.availableCourses ?? props.context.courses).filter(
    (course) => !course.isAddon,
  );
  // Several courses on one deal is an ordinary sale («Профи» + «Права»), and each is its own
  // enrollment — so the manager picks rather than the system guessing. При докупке выбор
  // не нужен, только если курс без зачисления один.
  const mustChoose = addon
    ? courses.length > 1
    : props.context.courseElementId === null && courses.length > 0;
  const singleAddon = addon && courses.length === 1 ? courses[0] : undefined;
  const [chosen, setChosen] = useState<number | null>(null);
  const blocked = missingFields.length > 0 || (mustChoose && chosen === null);

  return (
    // Карточка живёт в узкой шторке, а не на всю ширину экрана: раскладка считается от
    // контейнера (@container), иначе на 1440 px sm: включает две колонки и текст сжимается
    // в столбик рядом с кнопкой.
    <section
      className="pwz-sheet @container grid gap-4 border-l-4 border-l-primary p-4"
      data-testid="deal-enrollment-setup"
      aria-live="polite"
    >
      <div className="flex min-w-0 gap-3">
        <span
          className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl ${
            missingFields.length > 0
              ? "bg-warn-100 text-warn-500"
              : "bg-accent text-accent-foreground"
          }`}
          aria-hidden="true"
        >
          {missingFields.length > 0 ? <AlertCircle size={20} /> : <BookOpenCheck size={20} />}
        </span>
        <div className="min-w-0">
          <h2 className="pwz-heading text-base sm:text-lg">
            {missingFields.length > 0
              ? "Заполните сделку в amoCRM"
              : addon
                ? "Докуплен курс — добавьте его в план"
                : mustChoose
                  ? "В сделке несколько курсов"
                  : "Учебный план ещё не создан"}
          </h2>
          {missingFields.length > 0 ? (
            <div className="mt-1.5">
              <p className="text-sm text-ink-600">
                Без этих данных нельзя однозначно связать уроки со сделкой:
              </p>
              <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Незаполненные поля сделки">
                {missingFields.map((field) => (
                  <li
                    key={field}
                    className="rounded-md bg-warn-100 px-2 py-1 text-xs font-semibold text-warn-500"
                  >
                    {FIELD_LABELS[field]}
                  </li>
                ))}
              </ul>
            </div>
          ) : mustChoose ? (
            <div className="mt-1.5">
              <p className="max-w-2xl text-sm text-ink-600">
                {addon
                  ? "По этим курсам ещё нет уроков. Выберите, какой добавить в план сейчас; остальные — тем же способом."
                  : "Каждый курс — своё зачисление. Выберите, по какому создать уроки сейчас; второй добавите тем же способом."}
              </p>
              <fieldset className="mt-2 flex flex-col gap-1.5">
                <legend className="sr-only">Курс сделки</legend>
                {courses.map((course) => (
                  <label
                    key={course.elementId}
                    className="flex items-center gap-2 text-sm text-ink-900"
                  >
                    <input
                      type="radio"
                      name="deal-course"
                      value={course.elementId}
                      data-testid={`deal-course-${course.elementId}`}
                      checked={chosen === course.elementId}
                      onChange={() => setChosen(course.elementId)}
                    />
                    <span className="font-medium">{course.name ?? `Курс ${course.elementId}`}</span>
                    <span className="pwz-readout text-xs text-ink-600">
                      {courseSummary(course)}
                    </span>
                    {course.inCatalogCache ? null : (
                      <span className="text-xs text-warn-500">нет в каталоге</span>
                    )}
                  </label>
                ))}
              </fieldset>
            </div>
          ) : (
            <p className="mt-1.5 max-w-2xl text-sm text-ink-600">
              Создадим зачисление для «
              {singleAddon
                ? (singleAddon.name ?? `Курс ${singleAddon.elementId}`)
                : (props.context.courseName ?? "Курс")}
              » и подготовим уроки по плану курса. Время и площадку вы выберете следующим шагом.
            </p>
          )}
        </div>
      </div>
      <Button
        type="button"
        className="w-full"
        disabled={blocked || props.busy}
        onClick={() =>
          props.onCreate(
            mustChoose && chosen !== null
              ? chosen
              : // Докупка всегда называет курс явно: без этого сервер вернёт уже существующий план.
                (singleAddon?.elementId ?? undefined),
          )
        }
      >
        {props.busy ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
        {missingFields.length > 0
          ? "Создание недоступно"
          : props.busy
            ? "Создаём план…"
            : "Создать зачисление и уроки"}
      </Button>
    </section>
  );
}
